/**
 * ClosingSection — single closing block that merges the old FinalCTA and
 * Contact sections. Dark background, 50/50 split: emotional close on the
 * left, lead-capture form on the right. Phone fallback strip below.
 *
 * The form's behavior (state, validation, submit handler) is loaded
 * lazily once the section enters the viewport. Until then we render a
 * static placeholder with the same visual footprint to avoid CLS and
 * keep the landing entry chunk under budget.
 *
 * @module Pages/Homepage/sections/ClosingSection
 */

import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import "../../../i18n";

const DemoRequestForm = lazy(() => import("../components/DemoRequestForm"));

// Static placeholder rendered before the form chunk has loaded (or before
// the user has scrolled the section into view). Same visual footprint
// as the lazy form so no layout shift on hydration.
function FormPlaceholder() {
  const { t } = useTranslation("marketing");
  return (
    <div className="ul-drf ul-drf-dark" aria-hidden="true">
      <div className="ul-drf-field">
        <span className="ul-drf-label">{t("demoForm.roleLabel")}</span>
        <div className="ul-drf-skeleton" />
      </div>
      <div className="ul-drf-row">
        <div className="ul-drf-field">
          <span className="ul-drf-label">{t("demoForm.name")}</span>
          <div className="ul-drf-skeleton" />
        </div>
        <div className="ul-drf-field">
          <span className="ul-drf-label">{t("demoForm.email")}</span>
          <div className="ul-drf-skeleton" />
        </div>
      </div>
      <div className="ul-drf-row">
        <div className="ul-drf-field">
          <span className="ul-drf-label">{t("demoForm.phone")}</span>
          <div className="ul-drf-skeleton" />
        </div>
        <div className="ul-drf-field">
          <span className="ul-drf-label">{t("demoForm.institute")}</span>
          <div className="ul-drf-skeleton" />
        </div>
      </div>
      <div className="ul-drf-row">
        <div className="ul-drf-field">
          <span className="ul-drf-label">{t("demoForm.students")}</span>
          <div className="ul-drf-skeleton" />
        </div>
        <div className="ul-drf-field">
          <span className="ul-drf-label">{t("demoForm.message")}</span>
          <div className="ul-drf-skeleton" />
        </div>
      </div>
      <div className="ul-drf-skeleton-btn" />
    </div>
  );
}

function ClosingSection() {
  const { t } = useTranslation("marketing");
  const sectionRef = useRef(null);
  const [shouldLoadForm, setShouldLoadForm] = useState(false);

  // Load the form bundle once the section is within ~400px of the
  // viewport, OR after a 6s idle fallback for browsers without
  // IntersectionObserver. Keeps the landing entry chunk lean while
  // still feeling instant when the user reaches the close.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (typeof IntersectionObserver === "undefined") {
      const t = setTimeout(() => setShouldLoadForm(true), 6000);
      return () => clearTimeout(t);
    }
    const node = sectionRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoadForm(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="ul-section ul-closing-section"
      id="contact"
    >
      <div className="ul-closing-grid">
        <div className="ul-closing-left">
          <div className="ul-cta-eyebrow">{t("closing.eyebrow")}</div>
          <h2 className="ul-closing-headline">
            <Trans t={t} i18nKey="closing.headline" components={{ hl: <span /> }} />
          </h2>
          <p className="ul-closing-sub">
            {t("closing.sub")}
          </p>
          <ul className="ul-closing-trust">
            <li>{t("closing.trust.t1")}</li>
            <li>{t("closing.trust.t2")}</li>
            <li>{t("closing.trust.t3")}</li>
          </ul>
          <div className="ul-closing-fallback">
            {t("closing.preferToTalk")}{" "}
            <a href="tel:+917204404452">+91 72044 04452</a>
            <span className="ul-closing-fallback-sep"> · </span>
            <a href="mailto:contact@markszen.com">contact@markszen.com</a>
            <br />
            {t("closing.hours")}
          </div>
          <div className="ul-closing-teacher-link">
            {t("closing.haveAccount")}{" "}
            <Link to="/teacher-login">{t("closing.teacherLogin")}</Link>
          </div>
        </div>

        <div className="ul-closing-right">
          {shouldLoadForm ? (
            <Suspense fallback={<FormPlaceholder />}>
              <DemoRequestForm
                source="landing-page-inline"
                variant="dark"
                submitLabel={t("demoForm.submit")}
              />
            </Suspense>
          ) : (
            <FormPlaceholder />
          )}
        </div>
      </div>
    </section>
  );
}

export default ClosingSection;
